<template>
  <div>
    <EquipmentEditHeader
      v-if="isAdmin"
      data-cy="pump-search-view-edit-header"
    />
    <div v-if="flowrateBbld" class="mb-3">
      <div class="flex align-items-center gap-4">
        <div class="flex align-items-center field-checkbox">
          <Checkbox
            v-model="enableFilter"
            :input-id="`enable-flow-filter-${flowrateBbld}`"
            :disabled="isCalculatingEfficiencies"
            binary
          />
          <label :for="`enable-flow-filter-${flowrateBbld}`">
            Filter and Sort Pumps
          </label>
          <span v-if="isCalculatingEfficiencies" class="text-sm text-500 ml-2">
            Sorting by efficiency…
          </span>
        </div>

        <div
          v-if="showAutoAddHousingsToggle"
          class="flex align-items-center field-checkbox"
        >
          <Checkbox
            v-model="autoAddHousings"
            :input-id="`auto-add-housings-${flowrateBbld}`"
            binary
          />
          <label :for="`auto-add-housings-${flowrateBbld}`">
            Auto-add Housings
          </label>
        </div>
      </div>
    </div>
    <div v-if="noPumpsWithFilter" class="no-recommendation-message-container">
      <div class="no-recommendation-message">
        <p class="no-recommendation-heading">
          No pumps match this configuration.
        </p>
        <p class="no-recommendation-body">
          If you'd like to select a pump, please un-check
          <span class="no-recommendation-highlight"
            >"Filter and Sort Pumps"</span
          >.
        </p>
      </div>
    </div>
    <EquipmentTable
      v-else
      data-cy="pump-search-view-table"
      @row-select="handleRowSelect"
    />
  </div>
</template>

<script setup lang="ts">
import type { PropType } from "vue";
import { provide, ref, watch, computed, reactive } from "vue";
import { Sizing_Pumping_Type_Enum, type PumpsQuery } from "gql/api";
import { usePumps } from "@/composables/equipment/pumps/pumps";
import EquipmentEditHeader from "@/components/equipment/EquipmentEditHeader.vue";
import EquipmentTable from "@/components/equipment/EquipmentTable.vue";
import Checkbox from "primevue/checkbox";
import { injectStrict } from "@/utils/utils";
import {
  pumpsFormKey,
  equipmentFormKey,
  parametersFormKey,
  wellPropertiesFormKey,
  correlationsFormKey,
  sealsFormKey,
  intakesFormKey,
  scenarioFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { watchDebounced } from "@vueuse/core";
import useDestructureScenario from "@/composables/useDestructureScenario";
import { useScenarioCalculations } from "@/composables/scenarioCalculations/useScenarioCalculations";
import type { Scenario, XrefPumps } from "@/composables/forms/formTypes";

const EFFICIENCY_CACHE_MAX = 200;

const props = defineProps({
  isAdmin: { type: Boolean, default: false },
  flowrateBbld: { type: Number, default: null },
  pumpRowIndex: { type: Number, default: null },
  enableFlowRateFilter: { type: Boolean, default: false },
  pumpXref: {
    type: Object as PropType<XrefPumps[number]>,
    default: null
  }
});

// The pumps type can't be Pumps since the Pumps type (and the other equipment types)
// are taken directly from the gql schema. So for example the Intakes type is the same as
// IntakesQuery['intakes'][number], however since PumpsQuery has an
// alias for r_housings in it, it is different from the Pumps type that just has housings.
// So we use PumpsQuery['pumps'][number] instead since this bubbles up to updating XrefPumps
// which ultimately comes from SizingsQuery and it also has the alias for r_housings.
// TL;DR: Pumps is the same as PumpsQuery['pumps'][number] except for the r_housings alias,
// but we need to use the latter type since our formtypes are based on the query types.
const emit = defineEmits<{
  (
    event: "select",
    payload: { pump: PumpsQuery["pumps"][number]; autoAddHousings: boolean }
  ): void;
}>();

// These are for non-admin mode to calculate estimated efficiency
const pumpsForm = !props.isAdmin ? injectStrict(pumpsFormKey, null) : null;
const equipmentForm = !props.isAdmin
  ? injectStrict(equipmentFormKey, null)
  : null;
const parametersForm = !props.isAdmin
  ? injectStrict(parametersFormKey, null)
  : null;
const wellPropertiesForm = !props.isAdmin
  ? injectStrict(wellPropertiesFormKey, null)
  : null;
const correlationsForm = !props.isAdmin
  ? injectStrict(correlationsFormKey, null)
  : null;
const sealsForm = !props.isAdmin ? injectStrict(sealsFormKey, null) : null;
const intakesForm = !props.isAdmin ? injectStrict(intakesFormKey, null) : null;
const scenarioForm = !props.isAdmin ? injectStrict(scenarioFormKey) : null;

// Local state for filter checkbox
const enableFilter = ref(props.enableFlowRateFilter ?? false);

const selectedEquipmentId = ref("");

const isHps = computed(() => {
  return (
    !!scenarioForm &&
    scenarioForm.data.value?.pumping_type === Sizing_Pumping_Type_Enum.Hps
  );
});

// Auto-add housings toggle is currently only surfaced for HPS sizing.
// DEV NOTE: To enable this for non-HPS pumps, relax `isHps` below and/or
// promote this flag to a prop so other pumping types can opt-in.
const autoAddHousings = ref<boolean>(isHps.value);

const showAutoAddHousingsToggle = computed(() => !props.isAdmin && isHps.value);

async function handleRowSelect(event: { data: PumpsQuery["pumps"][number] }) {
  selectedEquipmentId.value = event.data.id;

  // For admin views, keep existing behavior and emit selection directly
  if (props.isAdmin) {
    emit("select", { pump: event.data, autoAddHousings: false });
    return;
  }

  // For HPS sizing, auto-confirm the pump selection without a separate
  // confirmation step while still allowing the auto-add housings toggle
  // to control housing creation in the pump row.
  // DEV NOTE: We could enable auto-confirm for non-HPS pumps by relaxing
  // the `isHps` guard here or moving this behavior behind a prop so
  // additional pumping types can opt-in.
  if (isHps.value) {
    emit("select", {
      pump: event.data,
      autoAddHousings: autoAddHousings.value
    });
  } else {
    emit("select", { pump: event.data, autoAddHousings: false });
  }
}

// Get pumpHz from parameters if available
const pumpHz = computed(() => {
  if (parametersForm?.data.value?.pump_hz) {
    return parametersForm.data.value.pump_hz;
  }
  return 60; // Default to 60Hz
});

// Extract casing data from well properties
const topCasingDriftInches = computed(() => {
  return wellPropertiesForm?.data.value?.r_top_casing?.drift_inches ?? null;
});

const bottomCasingDriftInches = computed(() => {
  return wellPropertiesForm?.data.value?.r_bottom_casing?.drift_inches ?? null;
});

const topCasingDepth = computed(() => {
  return wellPropertiesForm?.data.value?.top_casing_length_feet ?? null;
});

// Calculate pump depth from tubing lengths
const pumpDepth = computed(() => {
  const topTubingLength =
    wellPropertiesForm?.data.value?.top_tubing_length_feet ?? 0;
  const bottomTubingLength =
    wellPropertiesForm?.data.value?.bottom_tubing_length_feet ?? 0;
  const totalTubingLength = topTubingLength + bottomTubingLength;
  const datumPoint =
    wellPropertiesForm?.data.value?.well_test_datum_point_measured_depth_feet ??
    0;

  // If tubing lengths are defined, use them; otherwise fall back to datum point
  return totalTubingLength || datumPoint || null;
});

const useCompressionPumps = computed(() => {
  return parametersForm?.data.value?.has_compression_pump ?? false;
});

const pumpsearch = usePumps(props.isAdmin, {
  flowRateBBLD: props.flowrateBbld,
  enableFlowRateFilter: enableFilter.value,
  pumpHz: pumpHz.value,
  topCasingDriftInches: topCasingDriftInches.value ?? undefined,
  bottomCasingDriftInches: bottomCasingDriftInches.value ?? undefined,
  pumpDepth: pumpDepth.value ?? undefined,
  topCasingDepth: topCasingDepth.value ?? undefined,
  usingCompressionPumps: useCompressionPumps.value
});

// Watch for changes from parent component
watch(
  () => props.enableFlowRateFilter,
  newValue => {
    if (newValue !== undefined) {
      enableFilter.value = newValue;
    }
  },
  { immediate: true } // Ensure initial value is synced
);

// Watch for user toggling the checkbox and update the composable
watch(
  enableFilter,
  newValue => {
    pumpsearch.flowRateFilter.value = newValue;
  },
  { immediate: true } // Ensure initial value is synced
);

// Watch for flow rate changes from parent
watch(
  () => props.flowrateBbld,
  newValue => {
    if (newValue !== undefined) {
      pumpsearch.targetFlowRate.value = newValue;
    }
  }
);

// Watch for pumpHz changes and update composable; clear efficiency cache so cached values stay correct
watch(pumpHz, (newValue, oldValue) => {
  if (newValue !== undefined) {
    pumpsearch.pumpHz.value = newValue;
  }
  if (oldValue !== undefined && newValue !== oldValue) {
    clearEfficiencyCache();
  }
});

// Watch for topCasingDriftInches changes and update composable; clear efficiency cache so cached values stay correct
watch(
  topCasingDriftInches,
  (newValue, oldValue) => {
    if (newValue !== undefined && newValue !== null) {
      pumpsearch.topCasingDriftInches.value = newValue;
    } else {
      pumpsearch.topCasingDriftInches.value = 0;
    }
    if (oldValue !== undefined && newValue !== oldValue) {
      clearEfficiencyCache();
    }
  },
  { immediate: true } // Ensure initial value is synced
);

// Watch for bottomCasingDriftInches changes and update composable; clear efficiency cache so cached values stay correct
watch(
  bottomCasingDriftInches,
  (newValue, oldValue) => {
    if (newValue !== undefined && newValue !== null) {
      pumpsearch.bottomCasingDriftInches.value = newValue;
    } else {
      pumpsearch.bottomCasingDriftInches.value = 0;
    }
    if (oldValue !== undefined && newValue !== oldValue) {
      clearEfficiencyCache();
    }
  },
  { immediate: true } // Ensure initial value is synced
);

// Watch for pumpDepth changes and update composable; clear efficiency cache so cached values stay correct
watch(
  pumpDepth,
  (newValue, oldValue) => {
    if (newValue !== undefined && newValue !== null) {
      pumpsearch.pumpDepth.value = newValue;
    } else {
      pumpsearch.pumpDepth.value = 0;
    }
    if (oldValue !== undefined && newValue !== oldValue) {
      clearEfficiencyCache();
    }
  },
  { immediate: true } // Ensure initial value is synced
);

// Watch for topCasingDepth changes and update composable; clear efficiency cache so cached values stay correct
watch(
  topCasingDepth,
  (newValue, oldValue) => {
    if (newValue !== undefined && newValue !== null) {
      pumpsearch.topCasingDepth.value = newValue;
    } else {
      pumpsearch.topCasingDepth.value = 0;
    }
    if (oldValue !== undefined && newValue !== oldValue) {
      clearEfficiencyCache();
    }
  },
  { immediate: true } // Ensure initial value is synced
);

watch(
  () => props.pumpXref?.id,
  (newValue, oldValue) => {
    if (oldValue !== undefined && newValue !== oldValue) {
      clearEfficiencyCache();
    }
  }
);

// Cache efficiency and required-stages calculations (same hypothetical run per pump)
const efficiencyCache = reactive<Record<string, number | null>>({});
const requiredStagesCache = reactive<Record<string, number | null>>({});
const efficiencyCacheOrder: string[] = [];
const efficiencyCacheVersion = ref(0);

function getPumpCacheKey(pumpId: string): string {
  const pumpXrefId = props.pumpXref?.id;
  return pumpXrefId ? `${pumpXrefId}:${pumpId}` : "";
}

function clearEfficiencyCache(): void {
  efficiencyCacheOrder.forEach(key => {
    delete efficiencyCache[key];
    delete requiredStagesCache[key];
  });
  efficiencyCacheOrder.length = 0;
  efficiencyCacheVersion.value++;
}

function evictEfficiencyCacheIfNeeded(): void {
  if (
    efficiencyCacheOrder.length >= EFFICIENCY_CACHE_MAX &&
    efficiencyCacheOrder.length > 0
  ) {
    const oldestKey = efficiencyCacheOrder.shift();
    if (oldestKey !== undefined) {
      delete efficiencyCache[oldestKey];
      delete requiredStagesCache[oldestKey];
    }
  }
}

function getEfficiencyForPump(
  pump: PumpsQuery["pumps"][number]
): number | null {
  if (!props.pumpXref?.id) {
    return null;
  }

  const cacheKey = getPumpCacheKey(pump.id);

  // If we already ran hypothetical for this pump, return cached efficiency
  if (cacheKey in requiredStagesCache) {
    return efficiencyCache[cacheKey] ?? null;
  }

  evictEfficiencyCacheIfNeeded();

  if (
    !props.flowrateBbld ||
    !pumpsForm ||
    !equipmentForm ||
    !scenarioForm ||
    !parametersForm ||
    !wellPropertiesForm ||
    !correlationsForm ||
    !sealsForm ||
    !intakesForm ||
    !props.pumpXref
  ) {
    return null;
  }

  const manufacturer = pump.manufacturer?.toLowerCase() || "";
  const validManufacturers = ["summit", "hal"];
  const isSummitOrHal = validManufacturers.some(name =>
    manufacturer.includes(name)
  );

  try {
    const currentPumpsForm = pumpsForm?.data.value ?? {};
    const hypotheticalPumpsForm = { ...currentPumpsForm };

    // Replace the current xref row with a fresh hypothetical entry for this pump,
    // using the existing xref's ID so it slots into the correct position in pumps_order
    hypotheticalPumpsForm[props.pumpXref.id] = {
      __typename: "scenario_equipment_xref_pumps" as const,
      id: props.pumpXref.id,
      pump_id: pump.id,
      r_pump: pump,
      rate_correction_ratio: 1,
      head_correction_ratio: 1,
      brake_horsepower_correction_ratio: 1,
      total_stages: 1,
      r_xref_housings: [],
      housings_order: [],
      length_feet: 0,
      weight_pounds: 0
    } as XrefPumps[number];

    const hypotheticalPumpIds = Object.keys(hypotheticalPumpsForm);
    const existingPumpsOrder = equipmentForm?.data.value?.pumps_order || [];
    const pumpsOrder = existingPumpsOrder.filter(id =>
      hypotheticalPumpIds.includes(id)
    );

    if (!pumpsOrder.includes(props.pumpXref.id)) {
      pumpsOrder.push(props.pumpXref.id);
    }

    if (!pumpsOrder.length) {
      pumpsOrder.push(...hypotheticalPumpIds);
    }

    // Create modified scenario - leveraging existing form data
    const modifiedScenario = {
      ...scenarioForm?.data.value,
      parameters: { ...parametersForm?.data.value },
      well_properties: { ...wellPropertiesForm?.data.value },
      correlations: { ...correlationsForm?.data.value },
      equipment: {
        ...equipmentForm?.data.value,
        pumps_order: pumpsOrder,
        r_xref_pumps: Object.values(hypotheticalPumpsForm),
        r_xref_intakes: Object.values(intakesForm?.data.value || {}),
        r_xref_seals: Object.values(sealsForm?.data.value || {})
      }
    } as Scenario;

    // Use existing calculation pipeline - this is the key!
    const hypotheticalScenario = useDestructureScenario(modifiedScenario);
    const hypotheticalCalcs = useScenarioCalculations(hypotheticalScenario);

    const pumpCalc =
      hypotheticalCalcs.pumpCalculations.individual.value[props.pumpXref.id];
    const requiredStages = pumpCalc?.requiredStages ?? null;
    const efficiency = isSummitOrHal ? pumpCalc?.efficiencyRatio ?? null : null;

    requiredStagesCache[cacheKey] = requiredStages;
    efficiencyCache[cacheKey] = efficiency;
    efficiencyCacheOrder.push(cacheKey);
    return efficiency;
  } catch (error) {
    requiredStagesCache[cacheKey] = null;
    efficiencyCache[cacheKey] = null;
    efficiencyCacheOrder.push(cacheKey);
    return null;
  }
}

// Track if we're calculating efficiencies
const isCalculatingEfficiencies = ref(false);

// Pre-calculate efficiencies when rows are available, flowrate exists, and filter is enabled
// This follows the existing pattern: filtering/sorting only happens when "Filter and Sort Pumps" is checked
// Debounced to avoid excessive recalculations when dependencies change rapidly
watchDebounced(
  [
    () => pumpsearch.allRows.value,
    () => props.flowrateBbld,
    () => props.pumpXref?.id,
    enableFilter
  ],
  ([rows, flowrate, pumpXrefId]) => {
    if (!rows || rows.length === 0 || props.isAdmin) {
      return;
    }

    if (!pumpXrefId) {
      clearEfficiencyCache();
      return;
    }

    if (!flowrate) {
      clearEfficiencyCache();
      return;
    }
    // Pre-calculate efficiency and required stages when flowrate is set (Required Stages column + sort when filter on)

    // Calculate efficiencies for visible pumps (runs synchronously; async batching or worker could be a follow-up)
    isCalculatingEfficiencies.value = true;
    const pumpsToCalculate = rows.slice(0, 50);
    const updates: Array<{ cacheKey: string; efficiency: number | null }> = [];

    for (const pump of pumpsToCalculate) {
      const cacheKey = getPumpCacheKey(pump.id);
      if (!(cacheKey in efficiencyCache)) {
        try {
          const efficiency = getEfficiencyForPump(
            pump as unknown as PumpsQuery["pumps"][number]
          );
          updates.push({ cacheKey, efficiency });
        } catch (error) {
          console.warn(
            "[PumpSearchView] Error calculating efficiency for pump:",
            pump.id,
            error
          );
          updates.push({ cacheKey, efficiency: null });
        }
      }
    }

    if (updates.length > 0) {
      updates.forEach(({ cacheKey, efficiency }) => {
        efficiencyCache[cacheKey] = efficiency;
      });
      efficiencyCacheVersion.value++;
    }

    isCalculatingEfficiencies.value = false;
  },
  { immediate: true, debounce: 300 }
);

// Override allRows to sort by efficiency descending (highest first)
// This is the FINAL sort - only sort by efficiency when filter is enabled (following existing pattern)
// Efficiencies are calculated in the watch above, and this computed reacts to efficiencyCacheVersion
const sortedPumps = computed(() => {
  try {
    const rows = pumpsearch.allRows.value;

    if (!rows || rows.length === 0 || props.isAdmin) {
      return rows;
    }

    // Only sort by efficiency when filter is enabled AND flowrate is available
    // This follows the existing pattern: filtering/sorting only when "Filter and Sort Pumps" is checked
    if (props.flowrateBbld && enableFilter.value) {
      // Access efficiencyCacheVersion to ensure reactivity tracking
      // This ensures the computed re-runs when efficiencies are calculated/updated
      const cacheVersion = efficiencyCacheVersion.value;
      if (cacheVersion < 0) {
        return rows;
      }
      // Map pumps with efficiency and required stages from cache (calculated by watch)
      const pumpsWithEfficiency = rows.map(pump => {
        const cacheKey = getPumpCacheKey(pump.id);
        const cachedEfficiency =
          cacheKey in efficiencyCache ? efficiencyCache[cacheKey] : null;
        const cachedRequiredStages =
          cacheKey in requiredStagesCache
            ? requiredStagesCache[cacheKey]
            : null;
        const pumpWithExtras = {
          ...pump,
          pump_efficiency: cachedEfficiency,
          required_pump_stages: cachedRequiredStages
        };
        return { pump: pumpWithExtras, efficiency: cachedEfficiency };
      });

      // Pumps with numeric efficiency (including 0%) sorted by efficiency; null/undefined = no data
      const pumpsWithEfficiencyData = pumpsWithEfficiency.filter(
        p => p.efficiency !== null && typeof p.efficiency === "number"
      );
      const pumpsWithoutEfficiencyData = pumpsWithEfficiency.filter(
        p => p.efficiency === null || typeof p.efficiency !== "number"
      );

      // Sort pumps with efficiency data by efficiency DESCENDING (highest first)
      // This is the FINAL sort - ensures the first item is always the highest efficiency pump
      pumpsWithEfficiencyData.sort((a, b) => {
        if (
          a.efficiency !== null &&
          b.efficiency !== null &&
          typeof a.efficiency === "number" &&
          typeof b.efficiency === "number"
        ) {
          // DESCENDING: highest efficiency first
          return b.efficiency - a.efficiency;
        }
        return 0;
      });

      // Combine: pumps with efficiency data (sorted descending) first, then pumps without efficiency data
      // First item will always be the highest efficiency pump
      const sorted = [
        ...pumpsWithEfficiencyData,
        ...pumpsWithoutEfficiencyData
      ];

      return sorted.map(item => item.pump);
    }

    // If no flowrate, still add efficiency and required_stages (null) for display consistency
    return rows.map(pump => {
      const cacheKey = getPumpCacheKey(pump.id);
      return {
        ...pump,
        pump_efficiency:
          cacheKey in efficiencyCache ? efficiencyCache[cacheKey] : null,
        required_pump_stages:
          cacheKey in requiredStagesCache ? requiredStagesCache[cacheKey] : null
      };
    });
  } catch (error) {
    console.error("[PumpSearchView] Error sorting pumps by efficiency:", error);
    const rows = pumpsearch.allRows.value || [];
    return rows.map(pump => {
      const cacheKey = getPumpCacheKey(pump.id);
      return {
        ...pump,
        pump_efficiency:
          cacheKey in efficiencyCache ? efficiencyCache[cacheKey] : null,
        required_pump_stages:
          cacheKey in requiredStagesCache ? requiredStagesCache[cacheKey] : null
      };
    });
  }
});

// Override filteredRows to use our sorted pumps
// The table reads from filteredRows, so we need to override it directly
// When search/filter is active, intersect sorted with filtered IDs so zero matches shows none (not all rows)
const sortedFilteredRows = computed(() => {
  const sorted = sortedPumps.value || [];
  const filteredIds = new Set(
    (pumpsearch.filteredRows.value || []).map(p => p.id)
  );
  return sorted.filter(pump => filteredIds.has(pump.id));
});

// When Filter and Sort is ON and no pumps match the current configuration,
// show a helpful empty state instead of an empty table.
const noPumpsWithFilter = computed(() => {
  const filterOn = enableFilter.value;
  const count = sortedFilteredRows.value?.length ?? 0;
  const hasLoadedRows = Array.isArray(pumpsearch.allRows.value);

  // Only show the empty state once data has loaded; otherwise we may
  // momentarily show "No pumps match..." while the query is still in
  // flight or the table is initializing.
  return hasLoadedRows && filterOn && count === 0;
});

const equipmentWithSortedPumps = {
  ...pumpsearch,
  allRows: sortedPumps, // Override with sorted pumps
  filteredRows: sortedFilteredRows // Override filteredRows to use sorted data
};

provide("equipment", equipmentWithSortedPumps);

if (!props.isAdmin) {
  provide(
    "selected-equipment-id",
    computed(
      () => selectedEquipmentId.value || props.pumpXref?.r_pump?.id || ""
    )
  );
}
</script>
